import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { TicketType } from '@/types';
import { Copy, ExternalLink, Loader2, Trash2 } from 'lucide-react';
import { showSuccess, showError, confirmAction, showTextForManualCopy } from '@/lib/swal';
import { copyTextToClipboard } from '@/lib/clipboard-utils';
import { collection, getDocs, query, orderBy, deleteDoc, doc, setDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { formatDate } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import {
  buildPersistedRsvpLinkUrls,
  isEventDashboardRsvpLinkDoc,
  stripMaToShortUrlQuery,
} from '@/lib/personal-invite-links';

interface RSVPLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  unitId: string;
  tickets: TicketType[];
}

interface RSVPLink {
  id: string;
  ticketIds: string[];
  link: string;
  shortUrl?: string;
  clickCount: number;
  createdAt: Date;
  ticketNames: string[];
}

export default function RSVPLinkModal({ open, onOpenChange, eventId, unitId, tickets }: RSVPLinkModalProps) {
  const { currentUser } = useAuth();
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<string>>(new Set(tickets.map(t => t.id)));
  const [previousLinks, setPreviousLinks] = useState<RSVPLink[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  const toggleTicket = (ticketId: string) => {
    const newSelection = new Set(selectedTicketIds);
    if (newSelection.has(ticketId)) {
      newSelection.delete(ticketId);
    } else {
      newSelection.add(ticketId);
    }
    setSelectedTicketIds(newSelection);
  };

  const selectAll = () => {
    setSelectedTicketIds(new Set(tickets.map(t => t.id)));
  };

  const deselectAll = () => {
    setSelectedTicketIds(new Set());
  };

  const generateLink = (ticketIdsArray?: string[]) => {
    const baseUrl = `${window.location.origin}/unit/${unitId}/event/${eventId}/rsvp`;
    const idsToUse = ticketIdsArray || Array.from(selectedTicketIds);
    
    if (idsToUse.length === 0) {
      return baseUrl;
    }

    const ticketIds = idsToUse.join(',');
    return `${baseUrl}?tickets=${ticketIds}`;
  };

  const resolveTicketNames = (ticketIds: string[]): string[] =>
    ticketIds
      .map((id: string) => tickets.find(t => t.id === id)?.name)
      .filter(Boolean) as string[];

  const refreshLinks = useCallback(async () => {
    if (!eventId || !unitId || !currentUser) return;
    try {
      const linksRef = collection(db, 'units', unitId, 'events', eventId, 'rsvpLinks');
      const linksQuery = query(linksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(linksQuery);

      const links: RSVPLink[] = [];
      snapshot.forEach((d) => {
        const data = d.data();
        if (!isEventDashboardRsvpLinkDoc(data)) return;
        const tIds = data.ticketIds || [];
        const baseUrl = `${window.location.origin}/unit/${unitId}/event/${eventId}/rsvp`;
        const fallback = tIds.length > 0 ? `${baseUrl}?tickets=${tIds.join(',')}` : baseUrl;
        links.push({
          id: d.id,
          ticketIds: tIds,
          link: data.shortUrl || data.link || fallback,
          shortUrl: data.shortUrl,
          clickCount: data.clickCount || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
          ticketNames: tIds
            .map((id: string) => tickets.find(t => t.id === id)?.name)
            .filter(Boolean) as string[],
        });
      });

      setPreviousLinks(links);
    } catch (error: any) {
      console.error('Error fetching previous links:', error);
      if (error?.code !== 'permission-denied') {
        showError('Failed to load previous links. Please try again.');
      }
    }
  }, [eventId, unitId, currentUser, tickets]);

  useEffect(() => {
    if (!open || !eventId || !unitId || !currentUser) return;
    setLoadingLinks(true);
    refreshLinks().finally(() => setLoadingLinks(false));
  }, [open, eventId, unitId, currentUser, refreshLinks]);

  const handleCopyLink = async () => {
    if (selectedTicketIds.size === 0) {
      showError('Please select at least one ticket type');
      return;
    }

    if (!currentUser) {
      showError('You must be logged in to generate RSVP links');
      return;
    }

    const ticketIdsArray = Array.from(selectedTicketIds);
    const fallbackLink = generateLink(ticketIdsArray);
    setGeneratingLink(true);
    
    try {
      const createShortRsvpLink = httpsCallable(functions, 'createShortRsvpLink');
      const response = await createShortRsvpLink({
        unitId,
        eventId,
        ticketIds: ticketIdsArray,
        origin: window.location.origin,
        inviterPaysFor: true,
      });
      const result = response.data as {
        shortUrl?: string;
        targetUrl?: string;
        shortenerUsed?: boolean;
        linkId?: string;
      };
      const linkToCopy = stripMaToShortUrlQuery(
        result.shortUrl || result.targetUrl || fallbackLink,
      );

      const copied = await copyTextToClipboard(linkToCopy);
      if (copied) {
        if (result.shortenerUsed) {
          showSuccess('Short RSVP link copied to clipboard!');
        } else {
          showSuccess('RSVP link copied to clipboard (shortener unavailable)');
        }
      } else {
        await showTextForManualCopy('Copy this RSVP link', linkToCopy);
      }

      setPreviousLinks(prev => [{
        id: result.linkId || `local-${Date.now()}`,
        ticketIds: ticketIdsArray,
        link: linkToCopy,
        shortUrl: result.shortUrl,
        clickCount: 0,
        createdAt: new Date(),
        ticketNames: resolveTicketNames(ticketIdsArray),
      }, ...prev]);

      refreshLinks();
    } catch (error: any) {
      console.error('Error generating RSVP link:', error);
      const isPermDenied =
        error?.code === 'functions/permission-denied' ||
        error?.code === 'permission-denied';
      if (isPermDenied) {
        showError('You do not have permission to generate RSVP links. Please contact an administrator.');
      } else {
        try {
          const linkRef = doc(collection(db, 'units', unitId, 'events', eventId, 'rsvpLinks'));
          const persistedUrls = buildPersistedRsvpLinkUrls(fallbackLink, linkRef.id);
          await setDoc(linkRef, {
            ticketIds: ticketIdsArray,
            ...persistedUrls,
            clickCount: 0,
            createdAt: Timestamp.now(),
            shortenerProvider: 'ma-to',
            shortenerUsed: false,
            inviterPaysFor: true,
          });

          const copiedFallback = await copyTextToClipboard(persistedUrls.targetUrl);
          if (copiedFallback) {
            showSuccess('Short link unavailable, full RSVP link copied to clipboard');
          } else {
            await showTextForManualCopy('Copy this RSVP link', persistedUrls.targetUrl);
          }

          setPreviousLinks(prev => [{
            id: linkRef.id,
            ticketIds: ticketIdsArray,
            link: persistedUrls.targetUrl,
            clickCount: 0,
            createdAt: new Date(),
            ticketNames: resolveTicketNames(ticketIdsArray),
          }, ...prev]);

          refreshLinks();
        } catch (innerError) {
          console.error('Error saving fallback RSVP link:', innerError);
          showError('Failed to save RSVP link. Please try again.');
        }
      }
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyExistingLink = async (link: string) => {
    const copied = await copyTextToClipboard(link);
    if (copied) {
      showSuccess('Link copied to clipboard!');
    } else {
      await showTextForManualCopy('Copy this link', link);
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    if (!currentUser) {
      showError('You must be logged in to delete links');
      return;
    }

    const result = await confirmAction({
      title: 'Delete Link?',
      text: 'Are you sure you want to delete this link? This action cannot be undone.',
      confirmButtonText: 'Delete',
      icon: 'warning',
    });

    if (!result.isConfirmed) return;

    try {
      const linkRef = doc(db, 'units', unitId, 'events', eventId, 'rsvpLinks', linkId);
      await deleteDoc(linkRef);
      setPreviousLinks(previousLinks.filter(link => link.id !== linkId));
      showSuccess('Link deleted successfully');
    } catch (error: any) {
      console.error('Error deleting link:', error);
      if (error?.code === 'permission-denied') {
        showError('You do not have permission to delete this link.');
      } else {
        showError('Failed to delete link');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>RSVP Links</DialogTitle>
          <DialogDescription>
            Generate new RSVP links or copy previously generated links. Click counts show how many times each link has been accessed.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Previous Links Section */}
          {(previousLinks.length > 0 || loadingLinks) && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-foreground">Previously Generated Links</Label>
              <div className="max-h-64 divide-y divide-border overflow-y-auto rounded-lg border border-border">
                {loadingLinks && previousLinks.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Loading links...
                  </div>
                ) : (
                  previousLinks.map((link) => (
                    <div key={link.id} className="p-4 hover:bg-muted/50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium text-foreground">
                              {link.ticketNames.length > 0 
                                ? link.ticketNames.join(', ')
                                : 'All Tickets'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({formatDate(link.createdAt)})
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground font-mono break-all mb-2">
                            {link.link}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>
                              <ExternalLink className="inline h-3 w-3 mr-1" />
                              {link.clickCount} click{link.clickCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyExistingLink(link.link)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteLink(link.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Generate New Link Section */}
          <div className="space-y-4 border-t border-border pt-4">
            <Label className="text-sm font-semibold text-foreground">Generate New Link</Label>
            {tickets.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tickets configured for this event.</p>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <Label className="text-sm font-medium text-foreground">Select Ticket Types</Label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAll}>
                      Select All
                    </Button>
                    <Button variant="outline" size="sm" onClick={deselectAll}>
                      Deselect All
                    </Button>
                  </div>
                </div>
                
                <div className="max-h-64 space-y-3 overflow-y-auto rounded-lg border border-border p-4">
                  {tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="flex cursor-pointer items-start space-x-3 rounded-md p-3 hover:bg-muted/50"
                      onClick={() => toggleTicket(ticket.id)}
                    >
                      <Checkbox
                        id={`ticket-${ticket.id}`}
                        checked={selectedTicketIds.has(ticket.id)}
                        onChange={() => toggleTicket(ticket.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Label
                        htmlFor={`ticket-${ticket.id}`}
                        className="flex-1 cursor-pointer text-foreground"
                      >
                        <div className="font-medium text-foreground">{ticket.name}</div>
                        <div className="text-sm text-muted-foreground">
                          £{ticket.price.toFixed(2)}
                        </div>
                      </Label>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center justify-between border-t border-border pt-4">
                  <div className="text-sm text-muted-foreground">
                    {selectedTicketIds.size === 0
                      ? 'No tickets selected'
                      : `${selectedTicketIds.size} of ${tickets.length} ticket${tickets.length !== 1 ? 's' : ''} selected`}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                      Close
                    </Button>
                    <Button onClick={handleCopyLink} disabled={selectedTicketIds.size === 0 || generatingLink}>
                      {generatingLink ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Copy className="mr-2 h-4 w-4" />
                          Generate & Copy Link
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}



